import { Injectable } from "@angular/core";
import { forkJoin, Observable, of, ReplaySubject, throwError } from "rxjs";
import { Authorization } from "@interfaces/authorization";
import { resource } from "@util/resource";
import { catchError, distinctUntilChanged, first, map, mergeMap } from "rxjs/operators";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Account, AccountRegistration } from "@interfaces/account";
import { storage } from "@util/storage";
import { marker } from "@biesbjerg/ngx-translate-extract-marker";
import { Host } from "@interfaces/host";
import { EditSelfAccount, SelfAccount } from "../interfaces/dws/self-account";
import {
  WinerySubscriptionInfo,
  WINERY_SUBSCRIPTION_CODE,
} from "../interfaces/dws/winery-subscription";
import { isPermissionGrantedWithLegacyBridge } from "./permissions/permission-legacy-bridge";
import { DWSLanguagesService } from "./dws/dws-languages.service";

import { Base64 } from "@util/base64";
import { set } from "lodash";
import { tr } from "@util/tr";
import { toast } from "@web/util/toast";
import {
  classifyWineSuiteDeviceType,
  isSmartphoneUserAgent,
  isTabletUserAgent,
} from "@util/dws-device-classification";

declare let versionApp: any;
declare let buildId: any;


/** Subset of backend {@code UserDWSDetails} used after DWS login (JWT body + mapped fields). */
export interface UserDWSLoginDetails {
  accountName?: string;
  emailAddress?: string;
  wineryId?: string;
  accountExpiresAt?: string | Date | null;
  winerySubscriptions?: WinerySubscriptionInfo[];
  permissions?: string[];
  roles?: string[];
  /** Set from CRM details when account has SUPERADMIN system permission. */
  superAdmin?: boolean;
  [key: string]: unknown;
}

export interface Login {
  account: Account;
  authorization: Authorization;
  company: Host.Company;
  selfAccount: SelfAccount;
  userDWS: UserDWSLoginDetails | null;
  wineryId: string;
  /** Copy of {@link UserDWSLoginDetails.winerySubscriptions} from the last auth response (JWT body `details`). */
  winerySubscriptions?: WinerySubscriptionInfo[];
  /** Mirrors {@link Authorization.super_admin_mode} for quick checks in templates. */
  superAdminMode?: boolean;
}

/** Returned when primary auth succeeded but TOTP verification is required. */
export interface MfaChallenge {
  mfaRequired: true;
  mfaChallengeToken: string;
  email: string;
}

export type AuthLoginResult = Login | MfaChallenge;

export function isMfaChallenge(result: AuthLoginResult): result is MfaChallenge {
  return (result as MfaChallenge).mfaRequired === true;
}

interface LoginResponseDWS {
  idToken: string;
  wineryId: string;
  expire: Date;
  details: any;
  superAdminMode?: boolean;
  mfaRequired?: boolean;
  mfaChallengeToken?: string;
  email?: string;
}

interface LoginResponse {
  account: Account;
  expires_at: string;
  host_data: any;
}

marker(["Invalid credentials", "Unknown error"]);

@Injectable({
  providedIn: "root",
})
export class AccountsService {
  login$: ReplaySubject<Login | null> = new ReplaySubject<Login | null>(1);
  authorization$: ReplaySubject<Authorization | null> =
    new ReplaySubject<Authorization | null>(1);
  account$: ReplaySubject<Account | null> = new ReplaySubject<Account | null>(
    1
  );
  company$: ReplaySubject<Host.Company | null> =
    new ReplaySubject<Host.Company | null>(1);
  selfAccount$: ReplaySubject<SelfAccount | null> =
    new ReplaySubject<SelfAccount | null>(1);

  private cachedPermissions: string[] | null = null;
  cachedPermissions$: ReplaySubject<string[]> = new ReplaySubject<
    string[]
  >();

  // Ping status tracking
  private lastPingSuccess: boolean | null = null;
  private lastPingTimestamp: number | null = null;
  private lastPingError: any = null;
  pingStatus$: ReplaySubject<{
    success: boolean | null;
    timestamp: number | null;
    error: any;
  }> = new ReplaySubject<{
    success: boolean | null;
    timestamp: number | null;
    error: any;
  }>(1);

  constructor(private languages: DWSLanguagesService) {
    // Hydrate permission cache before login$ emits so HasPermissionGuard on F5
    // does not deny routes (WS-4271: Calendar → Dashboard).
    storage.get("login").then((l) => {
      this.syncCachedPermissionsFromLogin(l);
      this.login$.next(l);
    });
    this.login$.subscribe(async (l) => {
      this.syncCachedPermissionsFromLogin(l);
      this.authorization$.next(l ? l.authorization : null);
      this.account$.next(l ? l.account : null);
      this.company$.next(l ? l.company : null);
      this.selfAccount$.next(l ? l.selfAccount : null);
      this.languages.applyAccountLanguage(l?.selfAccount);
      await storage.set("login", l);
      await storage.set("is_admin", !!l?.authorization?.is_admin);
      await storage.set("is_winery", !!l?.authorization?.is_winery);
    });
    
    setInterval(() => {
      // Use enhanced ping that sends detailed browser information
      this.doPing();
    }, 60000);

  }

  /** Sync mirror of login permissions — must run before any await on login$. */
  private syncCachedPermissionsFromLogin(login: Login | null | undefined): void {
    this.cachedPermissions = login?.userDWS?.permissions || null;
    this.cachedPermissions$.next(this.cachedPermissions || []);
  }

  doPing() {
    // Check if injector is available before attempting ping
    const injector = (window as any)["injector"];
    if (!injector) {
      console.warn("Angular injector not available, skipping ping");
      return;
    }

    this.pingFromFrontend().subscribe(
      () => {
        // Update status on success
        this.updatePingStatus(true, null);
      },
      (error) => {
        if (error.status === 401 || error.status === 403) {
          this.updatePingStatus(false, error);
          // this.logout();
          toast(tr("Your session may have expired. Please log out and log in again."));
        } else if (error.message && error.message.includes("Angular injector is not available")) {
          // Skip ping if injector is not available
          console.warn("Skipping ping due to injector unavailability");
          return;
        } else {
          // Fallback to simple ping if enhanced ping fails
          this.ping().subscribe(
            () => {
              // Update status on fallback success
              this.updatePingStatus(true, null);
            },
            (fallbackError) => {
              if (fallbackError.status === 401 || fallbackError.status === 403) {
                this.updatePingStatus(false, fallbackError);
                // this.logout();
                toast(tr("Your session may have expired. Please log out and log in again."));
              } else if (fallbackError.message && fallbackError.message.includes("Angular injector is not available")) {
                // Skip ping if injector is not available
                console.warn("Skipping fallback ping due to injector unavailability");
                return;
              } else {
                this.updatePingStatus(false, fallbackError);
                toast(tr("Server temporarily unreachable, please check again later"));
              }
            }
          );
        }
      }
    );
  }

  logout() {
    this.login$.next(null);
  }

  /**
   * System Super Admin: granted only via {@code /account/super-admin/grant} (stored as permission SUPERADMIN, not a team role).
   */
  hasSuperAdminPermission(login: Login | null | undefined): boolean {
    if (login?.userDWS?.superAdmin === true) {
      return true;
    }
    return (
      login?.userDWS?.permissions?.some(
        (p: string) => String(p).toUpperCase() === "SUPERADMIN"
      ) ?? false
    );
  }

  /**
   * Privileged Super Admin UI session: SUPERADMIN permission and JWT super-admin mode.
   */
  hasActiveSuperAdminSession(login: Login | null | undefined): boolean {
    return (
      this.hasSuperAdminPermission(login) &&
      login?.authorization?.super_admin_mode === true
    );
  }

  /**
   * Account-level expiration date from auth payload (`details.accountExpiresAt`).
   * Returns null if not set or not parseable.
   */
  getAccountExpiresAt(login: Login | null | undefined): Date | null {
    const raw = login?.userDWS?.accountExpiresAt;
    if (!raw) {
      return null;
    }
    const timestamp = new Date(raw).getTime();
    if (Number.isNaN(timestamp)) {
      return null;
    }
    return new Date(timestamp);
  }

  /** True when account expiration date exists and is in the past. */
  isAccountSuspended(login: Login | null | undefined): boolean {
    const expiresAt = this.getAccountExpiresAt(login);
    if (!expiresAt) {
      return false;
    }
    return expiresAt.getTime() < Date.now();
  }

  private mapAuthResponse(response: HttpResponse<LoginResponseDWS>): AuthLoginResult {
    if (!response.body) {
      throw {
        type: response.type,
        message: "Unknown error",
        cause: response,
        status: response.status,
      };
    }
    const body = response.body;
    if (body.mfaRequired === true && body.mfaChallengeToken) {
      return {
        mfaRequired: true,
        mfaChallengeToken: body.mfaChallengeToken,
        email: body.email ?? "",
      };
    }
    const wineryId = body.wineryId;
    const superAdminMode = body.superAdminMode === true;
    const authHeader = response.headers.get("Authorization") as string;
    const details = (body.details ?? null) as UserDWSLoginDetails | null;
    const subs = details?.winerySubscriptions;
    const winerySubscriptions = Array.isArray(subs)
      ? subs.map((s) => ({ ...s }))
      : undefined;
    const isAdmin = this.hasSuperAdminPermission({ userDWS: details } as Login);
    return {
      wineryId: wineryId as unknown as string,
      account: body.details,
      userDWS: details,
      winerySubscriptions,
      superAdminMode,
      company: { id: wineryId } as Host.Company,
      authorization: {
        expires_at: body.expire,
        token: authHeader,
        winery_id: wineryId as unknown as string,
        is_admin: isAdmin,
        is_winery: true,
        super_admin_mode: superAdminMode,
      },
    } as Login;
  }

  private mapAuthResponseToLogin(response: HttpResponse<LoginResponseDWS>): Login {
    const result = this.mapAuthResponse(response);
    if (isMfaChallenge(result)) {
      throw {
        type: "mfa_required",
        message: "MFA required",
        cause: result,
      };
    }
    return result;
  }

  private enrichLoginWithHostAndSelf(data: Login): Observable<Login> {
    const preserveSubs = (login: Login): Login => ({
      ...login,
      winerySubscriptions:
        login.winerySubscriptions ??
        login.userDWS?.winerySubscriptions?.map((s) => ({ ...s })),
      userDWS: login.userDWS
        ? {
            ...login.userDWS,
            winerySubscriptions:
              login.winerySubscriptions ?? login.userDWS.winerySubscriptions,
          }
        : null,
    });
    return resource("v2://host")
      .authorization("bearer", data.authorization.token.replace("Bearer ", ""))
      .get<any>()
      .pipe(
        map((hostData) => {
          if (!hostData?.company) {
            return preserveSubs({
              ...data,
              account: hostData ? { ...hostData } : data.account,
            });
          }
          const company = { ...hostData.company };
          company.id = data.wineryId;
          return preserveSubs({
            ...data,
            company,
            account: { ...hostData },
          });
        }),
        catchError(() => of(preserveSubs(data))),
        mergeMap((withHost) =>
          resource("crm://self/account")
            .authorization("bearer", withHost.authorization.token.replace("Bearer ", ""))
            .get<SelfAccount>()
            .pipe(
              map((selfAccount) => preserveSubs({ ...withHost, selfAccount })),
              catchError(() => of(withHost))
            )
        )
      );
  }

  /**
   * After initial login, switch winery JWT. Super-admin mode is derived from SUPERADMIN permission on the backend.
   */
  selectLoginContext(wineryId: string, superAdminMode: boolean): Observable<Login> {
    return this.login$.pipe(
      first(),
      mergeMap((current) => {
        if (!current?.authorization?.token) {
          return throwError(() => ({
            type: "unauthorized",
            message: "Not logged in",
          }));
        }
        return resource("crm://account/select-login-context")
          .authorization("bearer", current.authorization.token.replace("Bearer ", ""))
          .post<LoginResponseDWS>({ wineryId, superAdminMode }, { observe: "response" })
          .pipe(
            map((response) => this.mapAuthResponseToLogin(response)),
            mergeMap((data) => this.enrichLoginWithHostAndSelf(data))
          );
      })
    );
  }

  login(email: string, password: string): Observable<AuthLoginResult> {
    const base64password = Base64.encode(password);
    return resource("crm://account/authenticate")
      .post<LoginResponseDWS>(
        { email, base64password },
        { observe: "response" }
      )
      .pipe(
        map((response) => this.mapAuthResponse(response)),
        mergeMap((data) =>
          isMfaChallenge(data) ? of(data) : this.enrichLoginWithHostAndSelf(data)
        ),
        catchError((e) => {
          if (e instanceof HttpErrorResponse) {
            if (e.status === 401) {
              return throwError({
                type: "unauthorized",
                message: "Invalid credentials",
                cause: e,
                status: e.status,
              });
            }
            if (e.status === 400) {
              return throwError({
                type: "notmigrate",
                message: tr("Account need migration"),
                cause: e,
                status: e.status,
              });
            }

            // Handle "No Route to Host" or other network errors
            if (e.error && (e.message.includes("No route to host") || e.message.includes("Network Error")) || e.status === 404) {
              return throwError({
                type: "network",
                message: tr("Cannot reach the server. Please check your network connection. If error persists, please contact support."),
                cause: e,
                status: e.status || 0,
              });
            }
          }
          return throwError({
            type: "unknown",
            message: "Unknown error",
            cause: e,
            status: e.status,
          });
        })
      );

  }

  loginWithMicrosoft(idToken: string): Observable<AuthLoginResult> {
    return resource("crm://account/authenticate-microsoft")
      .post<LoginResponseDWS>(
        { id_token: idToken },
        { observe: "response" }
      )
      .pipe(
        map((response) => this.mapAuthResponse(response)),
        mergeMap((data) =>
          isMfaChallenge(data) ? of(data) : this.enrichLoginWithHostAndSelf(data)
        ),
        catchError((e) => {
          if (e instanceof HttpErrorResponse) {
            if (e.status === 401) {
              return throwError({
                type: "unauthorized",
                message: "Invalid credentials",
                cause: e,
                status: e.status,
              });
            }
            if (e.error && (e.message?.includes("No route to host") || e.message?.includes("Network Error")) || e.status === 404) {
              return throwError({
                type: "network",
                message: tr("Cannot reach the server. Please check your network connection. If error persists, please contact support."),
                cause: e,
                status: e.status || 0,
              });
            }
          }
          return throwError({
            type: "unknown",
            message: "Unknown error",
            cause: e,
            status: e.status,
          });
        })
      );
  }

  loginWithGoogle(idToken: string): Observable<AuthLoginResult> {
    return resource("crm://account/authenticate-google")
      .post<LoginResponseDWS>(
        { id_token: idToken },
        { observe: "response" }
      )
      .pipe(
        map((response) => this.mapAuthResponse(response)),
        mergeMap((data) =>
          isMfaChallenge(data) ? of(data) : this.enrichLoginWithHostAndSelf(data)
        ),
        catchError((e) => {
          if (e instanceof HttpErrorResponse) {
            if (e.status === 401) {
              return throwError({
                type: "unauthorized",
                message: "Invalid credentials",
                cause: e,
                status: e.status,
              });
            }
            if (e.error && (e.message?.includes("No route to host") || e.message?.includes("Network Error")) || e.status === 404) {
              return throwError({
                type: "network",
                message: tr("Cannot reach the server. Please check your network connection. If error persists, please contact support."),
                cause: e,
                status: e.status || 0,
              });
            }
          }
          return throwError({
            type: "unknown",
            message: "Unknown error",
            cause: e,
            status: e.status,
          });
        })
      );
  }

  completeMfaLogin(mfaChallengeToken: string, code: string): Observable<Login> {
    return resource("crm://account/authenticate-mfa")
      .post<LoginResponseDWS>(
        { mfaChallengeToken, code },
        { observe: "response" }
      )
      .pipe(
        map((response) => this.mapAuthResponseToLogin(response)),
        mergeMap((data: Login) => this.enrichLoginWithHostAndSelf(data)),
        catchError((e) => {
          if (e instanceof HttpErrorResponse && e.status === 401) {
            return throwError({
              type: "unauthorized",
              message: tr("Invalid verification code"),
              cause: e,
              status: e.status,
            });
          }
          return throwError({
            type: "unknown",
            message: tr("Invalid verification code"),
            cause: e,
            status: e.status,
          });
        })
      );
  }

  async use(login: Login): Promise<void> {
    this.login$.next(login);
    await new Promise((r) => setTimeout(r, 40));
  }

  async getLogin(): Promise<Login | null> {
    return storage.get("login");
  }

  async getRole(): Promise<boolean | null> {
    return storage.get("role");
  }

  getAccount(): Observable<Account> {
    return this.account$.asObservable() as Observable<Account>;
  }

  checkPassword(email: string, currentPassword: string): Observable<boolean> {
    // API returns a string response, ignore the string content and check if request was successful
    return resource("crm://account/check-password").postText({
      email,
      base64password: Base64.encode(currentPassword),
    }).pipe(
      map((response: string) => {
        // API returns string but we ignore it, just return true if we got a response
        return true;
      }),
      catchError((e) => {
        if (e.status === 401) {
          console.warn("Password check failed - incorrect credentials");
          return of(false);
        }
        return throwError(e);
      })
    );
  }

  update(account: AccountRegistration): Observable<unknown> {
    return resource("v2://registrations").put({
      account: account,
    });
  }

  edit(editAccount: EditSelfAccount): Observable<{ token: string }> {
    return resource("crm://self/account/edit").post(editAccount);
  }

  ping(): Observable<boolean> {
    return resource("crm://ping").get<boolean>();
  }

  /**
   * Enhanced ping that sends detailed browser information to PingFromFrontendRestController
   */
  pingFromFrontend(): Observable<any> {
    return this.login$.pipe(
      first(),
      map(login => this.collectBrowserData(login)),
      mergeMap(browserData => resource("crm://ping-from-ws").post<any>(browserData))
    );
  }

  /**
   * Test method to manually trigger enhanced ping (useful for debugging)
   */
  testPingFromFrontend(): Observable<any> {
    console.log('Testing enhanced ping...');
    return this.pingFromFrontend().pipe(
      map(response => {
        console.log('Enhanced ping successful:', response);
        this.updatePingStatus(true, null);
        return response;
      }),
      catchError(error => {
        console.error('Enhanced ping failed:', error);
        this.updatePingStatus(false, error);
        return throwError(error);
      })
    );
  }

  /**
   * Update ping status and notify subscribers
   */
  private updatePingStatus(success: boolean, error: any = null): void {
    this.lastPingSuccess = success;
    this.lastPingTimestamp = Date.now();
    this.lastPingError = error;
    
    const status = {
      success: this.lastPingSuccess,
      timestamp: this.lastPingTimestamp,
      error: this.lastPingError
    };
    
    this.pingStatus$.next(status);
  }

  /**
   * Get current ping status
   */
  getPingStatus(): {
    success: boolean | null;
    timestamp: number | null;
    error: any;
  } {
    return {
      success: this.lastPingSuccess,
      timestamp: this.lastPingTimestamp,
      error: this.lastPingError
    };
  }

  /**
   * Get ping status as observable
   */
  getPingStatus$(): Observable<{
    success: boolean | null;
    timestamp: number | null;
    error: any;
  }> {
    return this.pingStatus$.asObservable();
  }

  /**
   * Check if last ping was successful
   */
  isLastPingSuccessful(): boolean | null {
    return this.lastPingSuccess;
  }

  /**
   * Get last ping timestamp
   */
  getLastPingTimestamp(): number | null {
    return this.lastPingTimestamp;
  }

  /**
   * Get last ping error
   */
  getLastPingError(): any {
    return this.lastPingError;
  }

  /**
   * Collects comprehensive browser and device information
   */
  private collectBrowserData(login: Login | null): any {
    const now = new Date();
    const navigator = window.navigator;
    const screen = window.screen;
    const location = window.location;
    const document = window.document;

    // Get current login data for account/winery IDs
    const accountId = login?.account?.id || '';
    const wineryId = login?.wineryId || '';

    // Collect plugins information
    const plugins: string[] = [];
    if (navigator.plugins) {
      for (let i = 0; i < navigator.plugins.length; i++) {
        plugins.push(navigator.plugins[i].name);
      }
    }

    // Collect memory information (if available)
    let memory: number | undefined;
    if ('memory' in navigator) {
      memory = (navigator as any).memory?.jsHeapSizeLimit;
    }

    // Collect connection information (if available)
    let connectionType = 'unknown';
    let onlineStatus = navigator.onLine;
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connectionType = connection?.effectiveType || connection?.type || 'unknown';
    }

    // Collect hardware concurrency
    const hardwareConcurrency = navigator.hardwareConcurrency || 0;

    // Collect max touch points
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    // Collect pixel ratio
    const pixelRatio = window.devicePixelRatio || 1;

    // Collect viewport dimensions
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    // Collect screen color depth
    const colorDepth = screen.colorDepth || 24;

    // Collect timezone offset
    const clientTimeZoneOffset = now.getTimezoneOffset();

    const deviceType = classifyWineSuiteDeviceType();

    // Detect browser name and version
    let browserName = 'unknown';
    let browserVersion = 'unknown';
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Chrome') && !userAgent.includes('Edge')) {
      browserName = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : 'unknown';
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : 'unknown';
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browserName = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : 'unknown';
    } else if (userAgent.includes('Edge')) {
      browserName = 'Edge';
      const match = userAgent.match(/Edge\/(\d+)/);
      browserVersion = match ? match[1] : 'unknown';
    }

    // Detect operating system
    let operatingSystem = 'unknown';
    if (userAgent.includes('Windows')) {
      operatingSystem = 'Windows';
    } else if (userAgent.includes('Mac')) {
      operatingSystem = 'macOS';
    } else if (userAgent.includes('Linux')) {
      operatingSystem = 'Linux';
    } else if (userAgent.includes('Android')) {
      operatingSystem = 'Android';
    } else if (userAgent.includes('iOS')) {
      operatingSystem = 'iOS';
    }

    // Generate session ID (simple implementation)
    const sessionId = this.getOrCreateSessionId();

    return {
      accountId,
      wineryId,
      userAgent: navigator.userAgent,
      pageUrl: location.href,
      referrerUrl: document.referrer || '',
      sessionId,
      browserName,
      browserVersion,
      operatingSystem,
      deviceType,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      languages: navigator.languages || [navigator.language],
      country: this.getCountryFromLanguage(navigator.language),
      city: '', // Would need geolocation API for this
      region: '', // Would need geolocation API for this
      latitude: null, // Would need geolocation API for this
      longitude: null, // Would need geolocation API for this
      clientTimestamp: now.getTime(),
      clientTimeZoneOffset,
      viewportWidth,
      viewportHeight,
      colorDepth,
      pixelRatio,
      connectionType,
      onlineStatus,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes',
      plugins,
      memory,
      hardwareConcurrency,
      maxTouchPoints,
      vendor: navigator.vendor,
      vendorSub: (navigator as any).vendorSub || '',
      productSub: (navigator as any).productSub || '',
      appName: navigator.appName,
      appVersion: navigator.appVersion,
      appCodeName: navigator.appCodeName,
      buildId: buildId || 'unknown',
      version: this.getAppVersion(),
      pingData: {
        // Additional custom ping data can be added here
        timestamp: now.toISOString(),
        userAgentParsed: this.parseUserAgent(userAgent),
        performanceInfo: this.getPerformanceInfo(),
        localStorageAvailable: this.isLocalStorageAvailable(),
        sessionStorageAvailable: this.isSessionStorageAvailable(),
        indexedDBAvailable: this.isIndexedDBAvailable(),
        webGLInfo: this.getWebGLInfo(),
        canvasFingerprint: this.getCanvasFingerprint()
      }
    };
  }

  /**
   * Get or create a session ID
   */
  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem('divinea_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('divinea_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Get country from language code
   */
  private getCountryFromLanguage(language: string): string {
    const parts = language.split('-');
    return parts.length > 1 ? parts[1].toUpperCase() : '';
  }
  /**
   * Get app version
   */
  private getAppVersion(): string {
    // Get version from global versionApp variable (injected via version.js)
    return versionApp || '1.0.0';
  }

  /**
   * Parse user agent for additional details
   */
  private parseUserAgent(userAgent: string): any {
    const maxTP =
      typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0;
    const plat =
      typeof navigator !== "undefined" ? navigator.platform || "" : "";
    const tablet = isTabletUserAgent(userAgent, maxTP, plat);
    const g = typeof window !== "undefined" ? (window as any) : {};
    const smartphone =
      !tablet &&
      isSmartphoneUserAgent(userAgent, {
        maxTouchPoints: maxTP,
        platform: plat,
        vendor: typeof navigator !== "undefined" ? navigator.vendor : undefined,
        opera: g.opera ? String(g.opera) : undefined,
      });
    return {
      raw: userAgent,
      isMobile: smartphone,
      isTablet: tablet,
      isDesktop: !tablet && !smartphone,
    };
  }

  /**
   * Get performance information
   */
  private getPerformanceInfo(): any {
    if (!window.performance) return null;
    
    const perf = window.performance;
    return {
      timing: perf.timing ? {
        navigationStart: perf.timing.navigationStart,
        loadEventEnd: perf.timing.loadEventEnd,
        domContentLoadedEventEnd: perf.timing.domContentLoadedEventEnd
      } : null,
      memory: (perf as any).memory ? {
        usedJSHeapSize: (perf as any).memory.usedJSHeapSize,
        totalJSHeapSize: (perf as any).memory.totalJSHeapSize,
        jsHeapSizeLimit: (perf as any).memory.jsHeapSizeLimit
      } : null
    };
  }

  /**
   * Check if localStorage is available
   */
  private isLocalStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Check if sessionStorage is available
   */
  private isSessionStorageAvailable(): boolean {
    try {
      const test = '__sessionStorage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Check if IndexedDB is available
   */
  private isIndexedDBAvailable(): boolean {
    return !!(window.indexedDB || (window as any).webkitIndexedDB || (window as any).mozIndexedDB);
  }

  /**
   * Get WebGL information
   */
  private getWebGLInfo(): any {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return null;

      const webglContext = gl as WebGLRenderingContext;
      return {
        vendor: webglContext.getParameter(webglContext.VENDOR),
        renderer: webglContext.getParameter(webglContext.RENDERER),
        version: webglContext.getParameter(webglContext.VERSION),
        shadingLanguageVersion: webglContext.getParameter(webglContext.SHADING_LANGUAGE_VERSION)
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Generate canvas fingerprint
   */
  private getCanvasFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';

      // Draw some text and shapes
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Canvas fingerprint', 2, 2);
      ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
      ctx.fillRect(0, 0, 100, 50);
      
      return canvas.toDataURL();
    } catch (e) {
      return '';
    }
  }

  /**
   * True if `permission` is granted, including legacy (CRM, ORDERS, …) ↔ `section.*` bridge.
   * For section-level view/edit rules, prefer `PermissionResolverService` where registered.
   */
  hasPermission(permission: string): boolean {
    return this.cachedPermissions
      ? isPermissionGrantedWithLegacyBridge(permission, this.cachedPermissions)
      : false;
  }

  /** True when the user has at least one of the given permissions (OR). */
  hasAnyPermission(permissions: string[]): boolean {
    if (!permissions?.length) {
      return true;
    }
    return permissions.some((p) => this.hasPermission(p));
  }

  hasCachedPermissions(): boolean {
    return this.cachedPermissions !== null;
  }

  /** Current permission strings from login (copy). */
  getCachedPermissions(): string[] {
    return this.cachedPermissions ? [...this.cachedPermissions] : [];
  }

  /**
   * Exact JWT permission match (no legacy↔section bridge).
   * WINERY_ADMIN in the list still grants any check.
   */
  hasRawPermission(permission: string): boolean {
    const p = this.cachedPermissions;
    if (!p?.length) {
      return false;
    }
    const want = permission.toUpperCase();
    if (p.some((x) => x.toUpperCase() === want)) {
      return true;
    }
    return p.some((x) => x.toUpperCase() === "WINERY_ADMIN");
  }

  async hasPermissionAsync(permission: string): Promise<boolean> {
    return this.cachedPermissions$
      .pipe(
        first(),
        map((permissions) =>
          isPermissionGrantedWithLegacyBridge(permission, permissions)
        )
      )
      .toPromise();
  }

  /**
   * Whether the current winery has an active row in {@code winery_subscriptions} for {@code subscriptionCode}
   * (server only returns currently valid rows; this also checks dates on the client when present).
   */
  hasWinerySubscription(subscriptionCode: string): Observable<boolean> {
    return this.login$.pipe(
      map((login) =>
        AccountsService.evaluateWinerySubscription(login, subscriptionCode)
      ),
      distinctUntilChanged()
    );
  }

  /** True iff there is an active PRO line in {@code winery_subscriptions} for this winery. */
  hasProWinerySubscription(): Observable<boolean> {
    return this.hasWinerySubscription(WINERY_SUBSCRIPTION_CODE.PRO);
  }

  /** Synchronous check when you already hold a {@link Login} (e.g. resolver). */
  static hasWinerySubscriptionForLogin(
    login: Login | null | undefined,
    subscriptionCode: string
  ): boolean {
    return AccountsService.evaluateWinerySubscription(login ?? null, subscriptionCode);
  }

  private static evaluateWinerySubscription(
    login: Login | null,
    subscriptionCode: string
  ): boolean {
    const want = subscriptionCode.trim().toUpperCase();
    const subs =
      login?.winerySubscriptions !== undefined
        ? login.winerySubscriptions
        : login?.userDWS?.winerySubscriptions;
    if (subs?.length) {
      const now = Date.now();
      return subs.some((s) => {
        const code = (s.subscriptionCode || "").trim().toUpperCase();
        if (code !== want) {
          return false;
        }
        return AccountsService.isWinerySubscriptionRowActive(s, now);
      });
    }
    // Cached login may predate winery_subscriptions on userDWS; fall back to legacy host flag when present.
    if (want === WINERY_SUBSCRIPTION_CODE.PRO) {
      const acc = login?.account as { dws_pro_enabled?: boolean } | undefined;
      const co = login?.company as { dws_pro_enabled?: boolean } | undefined;
      return acc?.dws_pro_enabled === true || co?.dws_pro_enabled === true;
    }
    return false;
  }

  private static isWinerySubscriptionRowActive(
    s: WinerySubscriptionInfo,
    nowMs: number
  ): boolean {
    if (s.validityStart != null && s.validityStart !== "") {
      const t = new Date(s.validityStart as string | Date).getTime();
      if (!Number.isNaN(t) && nowMs < t) {
        return false;
      }
    }
    if (s.validityEnd != null && s.validityEnd !== "") {
      const t = new Date(s.validityEnd as string | Date).getTime();
      if (!Number.isNaN(t) && nowMs > t) {
        return false;
      }
    }
    return true;
  }
}
